import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import showAlert from 'src/core/utils/showAlert';
import errorHandler from 'src/core/utils/errorHandler';
import { Store, ApiService } from 'src/data/services/Services';
import Translations from 'src/core/utils/Translations';
import logError from 'src/data/services/logError';
import { isApiError } from 'src/core/models/api/ApiError';
import Constants from 'src/core/utils/Constants';
import type { ApiFormSubmission } from 'src/core/models/api/ApiFormSubmission';

interface StoredSubmission extends Partial<ApiFormSubmission> {
	form_id: string | number;
}

interface FormState {
	submissions: StoredSubmission[];
	formParams: Record<string, unknown> | null;
	loginFromForm: boolean;
	isCancelLoading: boolean;
}

const initialState: FormState = {
	submissions: [],
	formParams: null,
	loginFromForm: false,
	isCancelLoading: false,
};

export const useFormStore = create(
	combine(initialState, (set, get) => ({
		init: async () => {
			const stored = await Store.getObject<StoredSubmission[]>(Constants.storageForms);
			if (stored) {
				set({ submissions: stored });
			}
		},

		saveForm: (submission: StoredSubmission) => {
			const subs = [...get().submissions];
			const index = subs.findIndex((s) => s.form_id === submission.form_id);

			if (index > -1) {
				subs[index] = submission;
			} else {
				subs.push(submission);
			}

			set({ submissions: subs, formParams: null });
			Store.save(Constants.storageForms, subs);
		},

		getSubmissionByFormID: (id: string | number) => {
			return get().submissions.find((s) => s.form_id === id);
		},

		getSubmissionByUser: async (id: string | number, token: string) => {
			try {
				const { data } = await ApiService.endpoints.forms.getSubmission({ formId: id, id: token });

				if (data && Object.keys(data).length > 0) {
					return data;
				} else {
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
				}
			} catch (error) {
				// The endpoint carries skipErrorToast, so this dialog is the only thing the user gets:
				// show the reason the server gave rather than a sentence that fits every failure.
				const message =
					isApiError(error) && typeof error.response?.data?.data === 'string' ? error.response.data.data : null;

				logError(error);
				errorHandler(message ?? Translations.errorSomethingWentWrong, message ? null : Translations.errorTryAgainLater);
			}
		},

		cancelSubmission: async (id: string | number, token: string, navigation: { pop: () => void }) => {
			set({ isCancelLoading: true });
			try {
				const { data } = await ApiService.endpoints.forms.deleteSubmission({ formId: id, id: token });

				if (data.status === 'ok') {
					const updated = get().submissions.filter((s) => s.form_id !== id);
					set({ submissions: updated, isCancelLoading: false });
					Store.save(Constants.storageForms, updated);

					showAlert({
						title: Translations.successUnsubscribedTitle,
						message: Translations.successUnsubscribedMessage,
						onConfirm: () => navigation.pop(),
					});
				} else {
					set({ isCancelLoading: false });
					errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
				}
			} catch (e) {
				set({ isCancelLoading: false });

				// The backend refuses a cancellation once the event's cancellation deadline has passed (422 with a
				// cancellation_deadline error).
				const response = (e as { response?: { status?: number; data?: { error?: Record<string, unknown> } } })?.response;
				if (response?.status === 422 && response.data?.error?.cancellation_deadline) {
					errorHandler(Translations.cancelDeadlinePassedTitle, Translations.cancelDeadlinePassedMessage);
					return;
				}

				console.error(e);
				errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
			}
		},

		// Submits a form's answers and returns the raw response so the screen handles its own UI.
		submit: async (args: {
			formId: string | number;
			answers: Record<string, unknown>;
			choices?: Record<string, string[]>;
			pushToken?: string | null;
			userToken?: string | null;
			previousSubmission?: string | number | null;
			onBehalf?: boolean;
		}) => {
			const { data } = await ApiService.endpoints.forms.submit(args);
			return data;
		},

		// Fetches a submission's answers (raw, no UI). Callers handle their own empty/prefill logic.
		getSubmission: async ({ formId, id }: { formId: string | number; id: string | number }) => {
			const { data } = await ApiService.endpoints.forms.getSubmission({ formId, id });
			return data;
		},

		// The registrations this user made for others (raw, no UI).
		listOnBehalf: async (eventIdentifier: string) => {
			const { data } = await ApiService.endpoints.submissions.listOnBehalf({ eventIdentifier });
			return data;
		},

		// Cancels someone else's registration by its own id. Unlike cancelSubmission it leaves the user's own
		// stored submission alone and shows no UI.
		cancelOnBehalf: async ({ formId, id }: { formId: string | number; id: string }) => {
			const { data } = await ApiService.endpoints.forms.deleteSubmission({ formId, id });
			return data;
		},

		// Fetches a form definition for an event.
		getForm: async ({ eventUuid, formId }: { eventUuid: string; formId: string | number }) => {
			const { data } = await ApiService.endpoints.events.getForm({ eventUuid, formId });
			return data;
		},

		// Sends a feedback message.
		sendFeedback: async (payload: Record<string, unknown>) => {
			const { data } = await ApiService.endpoints.forms.sendFeedback(payload);
			return data;
		},
	})),
);
